import Component from '../../js/_Component';

class Submenu {
	/** The element which toggles the visibility of the submenu. */
	private toggle: HTMLButtonElement;

	/** The element whose visibility is toggled. */
	private content: HTMLElement;

	/** Whether the submenu is open. */
	private expanded: boolean;

	constructor(toggle: HTMLButtonElement, content: HTMLElement) {
		this.toggle = toggle;
		this.content = content;
		this.expanded = false;

		this.toggle.addEventListener('click', () => this.toggleSubmenu());
	}

	toggleSubmenu(): void {
		this.expanded ? this.closeSubmenu() : this.openSubmenu();
	}

	openSubmenu(): void {
		this.expanded = true;
		this.toggle.setAttribute('aria-expanded', 'true');
		this.content.style.display = 'block';
	}

	closeSubmenu(): void {
		this.expanded = false;
		this.toggle.setAttribute('aria-expanded', 'false');
		this.content.style.display = 'none';
	}
}

export default class SidebarAtxU extends Component {
	/** The base class of the component. */
	private baseClass = 'c-sidebar';

	/** Menu items that are links. */
	private menuLinks: NodeListOf<HTMLAnchorElement>;

	/** Menu items that toggle the visibility of submenus. */
	private menuButtons: NodeListOf<HTMLButtonElement>;

	/** Submenu items that are links. */
	private submenuLinks: NodeListOf<HTMLAnchorElement>;

	/** Collection of references to Submenu instances, keyed by ID attribute. */
	private submenus: object;

	/** The select element which replaces the menu on small screens. */
	private mobileSelect: HTMLSelectElement;

	constructor(element: HTMLElement) {
		super(element);

		// Get the menu and submenu items
		this.element = element;
		this.menuLinks = element.querySelectorAll(`.${this.baseClass}__menu-link`);
		this.menuButtons = element.querySelectorAll(`.${this.baseClass}__menu-button`);
		this.submenuLinks = element.querySelectorAll(`.${this.baseClass}__submenu-link`);

		// Keep references to Submenu instances
		this.submenus = {};

		// Attach show/hide functionality to each submenu using the Submenu class
		Array.from(this.menuButtons).forEach((menuButton: HTMLButtonElement) => {
			const submenuId: string = menuButton.getAttribute('aria-controls');
			const submenu: HTMLElement = document.getElementById(submenuId);
			this.submenus[submenuId] = new Submenu(menuButton, submenu);
		});

		// Attach navigational behavior to the select element
		this.mobileSelect = element.querySelector(`.${this.baseClass}__select`);
		this.mobileSelect.addEventListener('input', (event) => {
			const href = (event.target as HTMLSelectElement).value;
			window.location.href = href;
		});

		// Mark the link with the current URL as active
		this.highlightActiveItem();
	}

	/** Finds the link which matches the current URL and marks it as active. */
	highlightActiveItem(): void {
		// Get all items in the menu
		const items = [
			...Array.from(this.menuLinks),
			...Array.from(this.menuButtons),
			...Array.from(this.submenuLinks),
		];

		// Look for the current URL in the menu links
		items.forEach((item) => {
			// Get the path, which is either within `href` or a `data-pseudo-href` depending on tag
			const path = item.tagName === 'A' ? item.getAttribute('href') : item.dataset.pseudoHref;

			// Only continue if the item's URL matches the current URL
			if (path !== window.location.pathname) return;

			// Set the active attribute
			item.setAttribute('data-active', '');

			// Find the active item in the mobile <select> menu
			const option: HTMLOptionElement = this.mobileSelect.querySelector(`[value="${path}"]`);

			if (option) {
				// Select the active option as the default
				option.selected = true;
			}

			// Check if this item is within a submenu
			const submenu = item.closest(`.${this.baseClass}__submenu`);

			if (submenu) {
				// Open the submenu to reveal the active item
				this.submenus[submenu.id].openSubmenu();

				// Find the parent of this submenu item
				const menuButton = this.element.querySelector(`[aria-controls="${submenu.id}"]`);

				// Set the active attribute
				menuButton.setAttribute('data-active', '');
			}
		});
	}
}

Component.register(SidebarAtxU, 'SidebarAtxU');
